import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTimezone } from "@/contexts/TimezoneContext";
import { zoneLabel } from "@/lib/timezone";
import TimezoneSelector from "@/components/TimezoneSelector";
import { toast } from "sonner";
import { Save, User as UserIcon, Bell, BellOff, Send, Bookmark, Star, Flame, Lock, ShieldCheck, ShieldAlert, RefreshCw, Crown, LayoutDashboard, Shield } from "lucide-react";

/**
 * Personal profile page — a signed-in Discord user sees ONLY their own data
 * and can edit their display name, bio, timezone, and DM preferences. There
 * is no way to reach anyone else's profile from here.
 */
export default function Profile() {
  const { user, loading, refresh, logout } = useAuth();
  const { timezone, isBase, setTimezone } = useTimezone();
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [dcCfg, setDcCfg] = useState({ bot_enabled: false });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || "");
      setBio(user.bio || "");
    }
    (async () => {
      try { const { data } = await api.get("/config/discord"); setDcCfg(data); } catch {}
      try { const { data } = await api.get("/user/streak"); setStats(data); } catch {}
    })();
  }, [user?.id]);

  // Surface ?relinked=1 / ?relink_error=... from the OAuth callback (one-shot).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("relinked") === "1") {
      toast.success("Discord account re-linked. Please verify your new identity below.");
      refresh();
      window.history.replaceState({}, "", "/profile");
    } else if (q.get("relink_error")) {
      const err = q.get("relink_error");
      toast.error(err === "discord_already_linked"
        ? "That Discord account is already linked to another user."
        : `Re-link failed: ${err}`);
      window.history.replaceState({}, "", "/profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adopt user's saved TZ on mount if it differs from the currently selected one.
  useEffect(() => {
    if (user?.preferred_timezone && user.preferred_timezone !== timezone) {
      setTimezone(user.preferred_timezone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Whenever the user changes their TZ from anywhere on the site while signed
  // in, mirror the selection to their account so it follows them across devices.
  useEffect(() => {
    if (!user) return;
    if (user.preferred_timezone === timezone) return;
    (async () => {
      try { await api.put("/user/preferences", { preferred_timezone: timezone }); await refresh(); } catch {}
    })();
  }, [timezone, user]);

  if (loading) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_banned) return <Navigate to="/banned" replace />;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/user/profile", { display_name: displayName, bio });
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const toggleNotify = async () => {
    setNotifSaving(true);
    try {
      await api.put("/user/preferences", { notify_via_discord: !user.notify_via_discord });
      await refresh();
      toast.success(user.notify_via_discord ? "DM reminders disabled" : "DM reminders enabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setNotifSaving(false); }
  };

  const sendTestDm = async () => {
    try { await api.post("/user/test-dm"); toast.success("Test DM sent — check Discord"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to DM"); }
  };

  const startRelink = async () => {
    try {
      const { data } = await api.get("/auth/discord/relink");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start re-link");
    }
  };

  const roles = user.roles || [];
  const badges = stats?.badges_earned || [];

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-24" data-testid="profile-page">
      <div className="flex items-center justify-between mb-8 pb-6 border-b border-[var(--ff-border)]">
        <div>
          <div className="label-eyebrow mb-1 text-[var(--ff-red)] flex items-center gap-2">
            <Lock size={12} /> ▮ MY PROFILE · PRIVATE
          </div>
          <h1 className="font-display text-4xl lg:text-5xl leading-none" data-testid="profile-heading">HELLO, {(user.display_name || user.discord_username || user.name || "friend").toUpperCase()}.</h1>
        </div>
        <button onClick={async () => { await logout(); window.location.href = "/"; }} className="btn-ghost text-xs" data-testid="profile-logout-btn">Sign Out</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Editor */}
        <form onSubmit={saveProfile} className="lg:col-span-2 surface-card p-8 space-y-6" data-testid="profile-editor">
          <div className="flex items-center gap-4 pb-4 border-b border-[var(--ff-border)]">
            {user.discord_avatar
              ? <img src={user.discord_avatar} alt="" className={`w-20 h-20 rounded-full ring-2 ${user.is_founding_member ? "ring-amber-400" : "ring-[#5865F2]"}`} />
              : <div className="w-20 h-20 rounded-full bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-3xl">{(user.discord_username || "?")[0]}</div>}
            <div className="min-w-0">
              <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">DISCORD IDENTITY</div>
              <div className="font-display text-2xl mt-1 flex items-center gap-2 flex-wrap" data-testid="profile-discord-username">
                {user.discord_username || "—"}
                {user.is_founding_member && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-amber-400 bg-amber-400/10 text-amber-300 text-[0.55rem] uppercase tracking-[0.15em] font-mono-ui"
                    data-testid="profile-founder-ribbon"
                    title={`Founding Member #${user.founding_member_number} — among the first 100 supporters.`}
                  >
                    <Crown size={10} /> Founding Member #{user.founding_member_number}
                  </span>
                )}
              </div>
              <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
                {roles.join(" · ") || "user"} · Joined {(user.created_at || "").slice(0, 10)}
              </div>
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={40}
              placeholder={user.discord_username || "Your display name"}
              className="w-full px-4 py-3 text-sm"
              data-testid="profile-display-name-input"
            />
            <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mt-1">
              Optional — shown across the site instead of your Discord name. Max 40 chars.
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Short Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={5}
              maxLength={500}
              placeholder="A sentence or two about yourself — visible only to you for now."
              className="w-full px-4 py-3 text-sm font-mono-ui leading-relaxed"
              data-testid="profile-bio-input"
            />
            <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mt-1 text-right">
              {bio.length} / 500
            </div>
          </div>

          <div className="pt-4 border-t border-[var(--ff-border)] flex items-center justify-between">
            <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
              Only you can see this data.
            </div>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="profile-save-btn">
              <Save size={14} /> {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </form>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="surface-card p-6" data-testid="profile-timezone-panel">
            <div className="label-eyebrow mb-3">▮ TIMEZONE</div>
            <TimezoneSelector />
            <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-3">
              Showing times in <span className="text-white">{zoneLabel(timezone)}</span>{!isBase && " · Base: US Eastern"}. Follows you across devices.
            </div>
          </div>

          <div className="surface-card p-6" data-testid="profile-notif-panel">
            <div className="label-eyebrow mb-3">▮ DM REMINDERS</div>
            <button
              onClick={toggleNotify}
              disabled={notifSaving || !user.discord_id || !dcCfg.bot_enabled}
              className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors ${user.notify_via_discord ? "bg-[#5865F2] text-white border-[#5865F2]" : "border-[var(--ff-border)] text-white hover:border-white"} disabled:opacity-40`}
              data-testid="profile-toggle-dm-btn"
            >
              {user.notify_via_discord ? <Bell size={14} /> : <BellOff size={14} />}
              {user.notify_via_discord ? "Reminders ON" : "Enable Reminders"}
            </button>
            {user.notify_via_discord && (
              <button onClick={sendTestDm} className="w-full mt-2 btn-ghost text-xs justify-center" data-testid="profile-test-dm-btn">
                <Send size={12} /> Send Test DM
              </button>
            )}
            <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-3">
              Pings you 15 min before + when saved shows go live.
            </div>
          </div>

          <div className="surface-card p-6" data-testid="profile-summary-panel">
            <div className="label-eyebrow mb-3">▮ AT A GLANCE</div>
            <div className="space-y-3 text-xs font-mono-ui">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[var(--ff-text-dim)]"><Bookmark size={12} /> Saved shows</span>
                <span className="text-white">{(user.saved_shows || []).length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[var(--ff-text-dim)]"><Star size={12} /> Followed hosts</span>
                <span className="text-white">{(user.saved_hosts || []).length}</span>
              </div>
              {stats && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-[var(--ff-text-dim)]"><Flame size={12} /> Current streak</span>
                  <span className="text-white">{stats.current_streak_weeks || 0} wk</span>
                </div>
              )}
              {badges.length > 0 && (
                <div className="pt-2 border-t border-[var(--ff-border)]">
                  <div className="text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mb-2">Badges</div>
                  <div className="flex flex-wrap gap-1">
                    {badges.map((b) => (
                      <span key={b.key} className="px-2 py-0.5 border border-[var(--ff-border)] text-[0.6rem] uppercase tracking-[0.12em]">
                        {b.emoji} {b.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="pt-4 mt-4 border-t border-[var(--ff-border)] flex flex-wrap gap-2">
              <Link to="/watchlist" className="btn-ghost text-[0.65rem] flex-1 justify-center" data-testid="profile-watchlist-link">
                <Bookmark size={11} /> Watchlist
              </Link>
              <Link to="/memberships" className="btn-ghost text-[0.65rem] flex-1 justify-center" data-testid="profile-memberships-link">
                <Crown size={11} /> Memberships
              </Link>
              {roles.includes("host") && (
                <Link to="/host-studio" className="btn-ghost text-[0.65rem] flex-1 justify-center" data-testid="profile-host-studio-link">
                  <UserIcon size={11} /> Host Studio
                </Link>
              )}
            </div>
          </div>

          {(user.role === "founder" || user.role === "admin" || user.role === "moderator") && (
            <div className="surface-card p-6 border-[var(--ff-red)]/60" data-testid="profile-admin-panel-card">
              <div className="label-eyebrow mb-3 text-[var(--ff-red)] flex items-center gap-2">
                <Shield size={12} /> ▮ {user.role === "founder" ? "FOUNDER" : user.role === "admin" ? "ADMIN" : "MODERATOR"} ACCESS
              </div>
              <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mb-4">
                {user.role === "founder" && "Full site admin panel, Discord Security, Memberships, HR, and Moderation tools."}
                {user.role === "admin" && "Discord Security, Site Settings, and Moderation tools."}
                {user.role === "moderator" && "User Blacklist, Warnings, and Recent Moderation Activity."}
              </div>
              <Link
                to="/dashboard"
                className="btn-primary w-full justify-center"
                data-testid="profile-admin-panel-btn"
              >
                <LayoutDashboard size={14} /> Open {user.role === "founder" ? "Admin" : user.role === "admin" ? "Control" : "Mod"} Panel
              </Link>
            </div>
          )}

          <div className="surface-card p-6" data-testid="profile-verify-panel">
            <div className="label-eyebrow mb-3">▮ VERIFICATION</div>
            {user.is_verified ? (
              <div className="flex items-start gap-3">
                <ShieldCheck size={28} className="text-green-400 mt-0.5 shrink-0" />
                <div>
                  <div className="font-display text-sm" data-testid="profile-verified-badge">Verified ✓</div>
                  <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
                    {user.verified_at ? `Since ${new Date(user.verified_at).toLocaleDateString()}` : "Confirmed identity"}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3 mb-4">
                  <ShieldAlert size={28} className="text-yellow-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-display text-sm">Not verified</div>
                    <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
                      Prove control of your Discord to unlock the Members role.
                    </div>
                  </div>
                </div>
                <Link to="/verify" className="btn-primary w-full justify-center" data-testid="profile-verify-btn">
                  <ShieldCheck size={14} /> Verify Me
                </Link>
              </>
            )}
          </div>

          <div className="surface-card p-6" data-testid="profile-relink-panel">
            <div className="label-eyebrow mb-3">▮ DISCORD LINK</div>
            <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mb-4">
              Currently linked as <span className="text-white">{user.discord_username || "—"}</span>. Migrate your watchlist, streaks, and preferences to a different Discord account without losing them.
            </div>
            <button
              onClick={startRelink}
              disabled={!dcCfg.enabled}
              className="btn-ghost w-full justify-center text-xs"
              data-testid="profile-relink-btn"
            >
              <RefreshCw size={13} /> Re-link Discord
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
